import * as R from 'remeda'

import { Address } from './types'

export const API_GATEWAY_TIMEOUT_SECONDS = 120
export const API_GATEWAY_TIMEOUT_MS = 1000 * API_GATEWAY_TIMEOUT_SECONDS

// Lambda timeout values must be shorter than API_GATEWAY_TIMEOUT_SECONDS
export enum LambdaTimeout {
  Default = 30,
  Slow = 60,
  Max = API_GATEWAY_TIMEOUT_SECONDS - 1,
}

export const delayedPromise = <T>(timeout: number, value: T, rejectPromise = false): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (rejectPromise) {
        reject(new Error('Error', { cause: value }))
      } else {
        resolve(value)
      }
      clearTimeout(timeoutId)
    }, timeout)
  })
}

export const removeWhitespace = (str: string): string => str.replace(/\s/g, '')

export const stringToBoolean = (value: string | undefined): boolean => {
  return value?.trim()?.toLowerCase() === 'true'
}

export const assertValue = <T>(value: T | null | undefined, param: string): NonNullable<T> => {
  if (value === undefined || value === null) {
    throw new Error(`Unexpected null or undefined value for '${param}'`)
  }
  return value
}

export const removeUndefinedValues = <T>(object: {
  [key: string]: T | undefined
}): Required<{
  [key: string]: T
}> => {
  const entries = Object.entries(object).filter(([key, value]) => key !== undefined && value !== undefined)
  const o = R.fromEntries(entries)
  return o as Required<{
    [key: string]: T
  }>
}

type FalsyType = false | null | undefined | '' | 0

export const typedBoolean = <ValueType>(value: ValueType): value is Exclude<ValueType, FalsyType> => {
  return Boolean(value)
}

export const isValidAddress = (address: Address): boolean => {
  const { domesticAddress, foreignAddress } = address
  if (domesticAddress) {
    return !!(domesticAddress.street && domesticAddress.postalCode && domesticAddress.postOffice)
  }
  return !!(foreignAddress?.street && foreignAddress?.countryName)
}

export const isNationalityUsa = (nationalities: string[]): boolean => {
  return nationalities.some((nationality) => nationality.toUpperCase() === 'US')
}

export const roundNumberToFractionDigits = (number: number, fractionDigits = 2): number => {
  return parseFloat(number.toFixed(fractionDigits))
}

/**
 * Tests if the given value is defined by the given enum type.
 * @param enumValue value to test
 * @param enumType enum type to test against
 * @returns True if the given value is defined by the given enum, otherwise false.
 */
export const isEnumValue = <EV, ET extends { [key: string]: EV }>(
  enumValue: EV,
  enumType: ET,
): enumValue is ET[keyof ET] => {
  return Object.values(enumType).includes(enumValue)
}

/**
 * Tests if the contents of the given arrays are equal. Order of the array
 * elements do not matter in the comparison.
 * @param array1 first array to compare
 * @param array2 second array to compare
 * @returns True if the contents of the arrays are equal, otherwise false.
 */
export const areArrayContentsEqual = <T extends string | number | boolean>(
  array1: T[] | null | undefined,
  array2: T[] | null | undefined,
): boolean => {
  if (!array1 || !array2) {
    return false
  }
  if (array1.length !== array2.length) {
    return false
  }
  const serializeArray = (arr: T[]): string => {
    return arr.slice().sort().join()
  }
  const serializedArray1 = serializeArray(array1)
  const serializedArray2 = serializeArray(array2)
  return serializedArray1 === serializedArray2
}
